/**
 * Shared frontend types mirroring API response shapes.
 *
 * These types are simplified versions of the API models, representing
 * the data shapes returned by REST endpoints and consumed by React components.
 */

// ── Common ──────────────────────────────────────────────────

export type SortDirection = 'asc' | 'desc';

export interface DateRange {
  start: string;
  end: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ── Auth ────────────────────────────────────────────────────

export enum UserRole {
  Admin = 'Admin',
  ITAdmin = 'IT Admin',
  ContractSpecialist = 'Contract Specialist',
  FacilitiesAdmin = 'Facilities Admin',
  Accounting = 'Accounting',
  Controller = 'Controller',
  CFO = 'CFO',
  VPApprover = 'VP Approver',
  ServiceDesk = 'Service Desk',
}

export interface AuthContext {
  userId: string;
  email: string;
  displayName: string;
  roles: UserRole[];
  departmentScopes: string[];
  isAdmin: boolean;
}

// ── Vendor ──────────────────────────────────────────────────

export enum RiskTier {
  Critical = 'Critical',
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
}

export enum DataHandlingClass {
  PII = 'PII',
  FinancialData = 'Financial Data',
  PHI = 'PHI',
  None = 'None',
}

export interface Vendor {
  vendorId: number;
  vendorName: string;
  normalizedName: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  vendorCategory: string | null;
  departmentScope: string;
  riskTier: RiskTier;
  dataHandlingClass: DataHandlingClass | null;
  erpVendorId: string | null;
  supportPhone: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface VendorContact {
  contactId: number;
  vendorId: number;
  contactName: string;
  email: string | null;
  phone: string | null;
  contactRole: string;
  notes: string | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Contract ────────────────────────────────────────────────

export enum ContractLifecycleStatus {
  Draft = 'Draft',
  UnderReview = 'Under Review',
  Active = 'Active',
  Expiring = 'Expiring',
  Expired = 'Expired',
  Renewed = 'Renewed',
  Terminated = 'Terminated',
  Archived = 'Archived',
}

export interface Contract {
  contractId: number;
  vendorId: number;
  contractTitle: string;
  contractType: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  autoRenewalTerms: string | null;
  cancellationNoticePeriodDays: number | null;
  contractValue: number | null;
  paymentTerms: string | null;
  budgetCategory: string | null;
  glCode: string | null;
  departmentScope: string;
  lifecycleStatus: ContractLifecycleStatus;
  egnyteFilePath: string | null;
  egnyteFileId: string | null;
  notes: string | null;
  erpPoNumber: string | null;
  erpGlCode: string | null;
  renewalLinkedContractId: number | null;
  aiAssistedCreation: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface Amendment {
  amendmentId: number;
  contractId: number;
  amendmentDate: string;
  description: string | null;
  valueImpact: number | null;
  egnyteFilePath: string | null;
  egnyteFileId: string | null;
  createdAt: string;
  createdBy: string;
}

// ── Compliance ──────────────────────────────────────────────

export enum Severity {
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
}

export interface ComplianceDocument {
  complianceDocId: number;
  vendorId: number;
  documentType: string;
  issueDate: string | null;
  expirationDate: string | null;
  egnyteFilePath: string | null;
  egnyteFileId: string | null;
  verificationStatus: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RiskAlert {
  riskAlertId: number;
  vendorId: number;
  sourceName: string;
  detectionDate: string;
  signalType: string;
  summary: string | null;
  sourceUrl: string | null;
  severity: Severity;
  isResolved: boolean;
  resolutionNotes: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
}

// ── Invoice ─────────────────────────────────────────────────

export enum InvoiceLifecycleStatus {
  PendingReview = 'Pending Review',
  PendingVPApproval = 'Pending VP Approval',
  Approved = 'Approved',
  Paid = 'Paid',
  Rejected = 'Rejected',
  Disputed = 'Disputed',
}

export interface InvoiceRecord {
  invoiceId: number;
  vendorId: number | null;
  contractId: number | null;
  vendorName: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  subtotal: number | null;
  tax: number | null;
  totalAmount: number | null;
  paymentTerms: string | null;
  poNumber: string | null;
  lifecycleStatus: InvoiceLifecycleStatus;
  departmentScope: string;
  sourceType: string;
  // Source email metadata (Requirement 44.9)
  sourceMailbox: string | null;
  sourceEmailSubject: string | null;
  sourceEmailSender: string | null;
  sourceEmailDate: string | null;
  sourceEmailMessageId: string | null;
  sourceEmailAttachmentCount: number | null;
  // Egnyte reference (Requirement 44.29)
  egnyteFilePath: string | null;
  egnyteFileId: string | null;
  isDuplicate: boolean;
  aiAssistedCreation: boolean;
  aiConfidenceScores: string | null;
  codedBy: string | null;
  codedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionNotes: string | null;
  codingGLCode: string | null;
  codingBranch: string | null;
  codingSummary: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceLineItem {
  lineItemId: number;
  invoiceId: number;
  description: string | null;
  quantity: number | null;
  unitPrice: number | null;
  amount: number | null;
  glCode: string | null;
  costCenter: string | null;
  budgetCategory: string | null;
  coderInitials: string | null;
  siteNumber: string | null;
  aiSuggestedGLCode: string | null;
  aiSuggestedCostCenter: string | null;
  aiSuggestedBudgetCategory: string | null;
  aiSuggestedSiteNumber: string | null;
  aiSuggestedCategory: string | null;
  aiConfidence: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ── Site ────────────────────────────────────────────────────

export enum ABCCategory {
  A = 'A',
  B = 'B',
  C = 'C',
}

export interface SiteLocation {
  siteNumber: string;
  branchName: string;
  address: string | null;
  city: string | null;
  state: string | null;
  divisionCode: string | null;
  divisionName: string | null;
  regionCode: string | null;
  regionName: string | null;
  realEstateStatus: string | null;
  leaseStartDate: string | null;
  leaseExpirationDate: string | null;
  merakiDeploymentStatus: string | null;
  siteType: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BranchRanking {
  rankingId: number;
  siteNumber: string;
  invoiceYear: number;
  annualSalesAmount: number | null;
  salesRank: number | null;
  isActive: boolean;
  abcCategory: ABCCategory | null;
  createdAt: string;
  updatedAt: string;
}

// ── Circuit ─────────────────────────────────────────────────

export enum CircuitLifecycleStatus {
  Ordered = 'Ordered',
  InProgress = 'In Progress',
  Installed = 'Installed',
  Active = 'Active',
  PendingDisconnect = 'Pending Disconnect',
  Disconnected = 'Disconnected',
}

export interface DataCircuit {
  circuitId: number;
  siteNumber: string;
  circuitIdentifier: string | null;
  accountNumber: string | null;
  carrierVendor: string | null;
  vendorId: number | null;
  contractId: number | null;
  accessType: string | null;
  bandwidthMbps: number | null;
  encapsulation: string | null;
  wanIPAddress: string | null;
  subnet: string | null;
  mrc: number | null;
  slaStatus: string | null;
  circuitTypeClass: string | null;
  primarySecondary: string | null;
  wanAssignment: string | null;
  wirelessFailover: boolean;
  finalDesignFlag: boolean;
  lifecycleStatus: CircuitLifecycleStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Bandwidth ───────────────────────────────────────────────

export interface BandwidthSnapshot {
  snapshotId: number;
  siteNumber: string;
  snapshotDate: string;
  bandwidthByCarrier: string | null;
  wan1SpeedMbps: number | null;
  wan2SpeedMbps: number | null;
  totalBandwidthMbps: number | null;
  primaryCircuitDetails: string | null;
  secondaryCircuitDetails: string | null;
  createdAt: string;
}

export interface SnapshotTrendItem {
  snapshotDate: string;
  wan1SpeedMbps: number | null;
  wan2SpeedMbps: number | null;
  totalBandwidthMbps: number | null;
}

export interface AggregateAnalytics {
  avgWan1SpeedMbps: number | null;
  avgWan2SpeedMbps: number | null;
  totalCircuitCount: number;
}

export interface CarrierDistributionItem {
  carrier: string;
  circuitCount: number;
}

export interface UnderservedLocation {
  siteNumber: string;
  branchName: string;
  totalBandwidthMbps: number | null;
  wan1SpeedMbps: number | null;
  wan2SpeedMbps: number | null;
  thresholdMbps: number;
}

export interface BandwidthRankingComparison {
  siteNumber: string;
  branchName: string;
  abcCategory: string | null;
  annualSalesAmount: number | null;
  totalBandwidthMbps: number | null;
  wan1SpeedMbps: number | null;
  wan2SpeedMbps: number | null;
  snapshotDate: string | null;
}

// ── Asset ───────────────────────────────────────────────────

export enum AssetLifecycleStatus {
  InUse = 'In Use',
  Inventory = 'Inventory',
  Disposed = 'Disposed',
  Decommissioned = 'Decommissioned',
  Missing = 'Missing',
}

export enum DeviceType {
  Laptop = 'Laptop',
  Desktop = 'Desktop',
  Workstation = 'Workstation',
  Unknown = 'Unknown',
}

export interface HardwareAsset {
  assetId: number;
  serialNumber: string;
  deviceName: string | null;
  manufacturer: string | null;
  model: string | null;
  lifecycleStatus: AssetLifecycleStatus;
  siteNumber: string | null;
  deploymentDate: string | null;
  firstDeployedDate: string | null;
  acquisitionDate: string | null;
  costBasis: number | null;
  assignedUser: string | null;
  lastLoggedInUser: string | null;
  lastContactDate: string | null;
  deviceType: DeviceType | null;
  deviceRole: string | null;
  os: string | null;
  ipAddresses: string | null;
  ninjaOneId: string | null;
  intuneDeviceId: string | null;
  tdxAssetId: string | null;
  needsDetailRefresh: boolean;
  locationSource: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MerakiSubnetEntry {
  subnetId: number;
  subnetRange: string;
  siteNumber: string;
  networkName: string | null;
  vlanId: string | null;
  createdAt: string;
}

export interface DeviceToEmployeeRatio {
  siteNumber: string;
  deviceCount: number;
  employeeCount: number;
  ratio: number | null;
}

export interface RefreshForecastYear {
  year: number;
  laptopCount: number;
  desktopCount: number;
  workstationCount: number;
  totalCount: number;
  estimatedCost: number;
}

export interface RefreshForecastResult {
  forecast: RefreshForecastYear[];
  thresholds: { laptop: number; desktop: number; workstation: number };
  averageCosts: { laptop: number; desktop: number; workstation: number };
}

// ── Budget ──────────────────────────────────────────────────

export interface Budget {
  budgetId: number;
  fiscalYear: number;
  budgetName: string | null;
  approvalStatus: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface BudgetLineItem {
  lineItemId: number;
  budgetId: number;
  glCode: string | null;
  description: string | null;
  budgetCategory: string | null;
  vendorId: number | null;
  siteNumber: string | null;
  month01: number;
  month02: number;
  month03: number;
  month04: number;
  month05: number;
  month06: number;
  month07: number;
  month08: number;
  month09: number;
  month10: number;
  month11: number;
  month12: number;
  annualTotal: number;
  notes: string | null;
}

// ── Employee ────────────────────────────────────────────────

export enum EmployeeCategory {
  CorporateOnsite = 'Corporate Onsite',
  CorporateRemote = 'Corporate Remote',
  TradeWinds = 'TradeWinds',
  Regional = 'Regional',
  Sales = 'Sales',
  Operations = 'Operations',
  Branch = 'Branch',
}

export interface EmployeeRecord {
  employeeId: number;
  bakerId: string;
  legalName: string | null;
  jobTitle: string | null;
  businessTitle: string | null;
  workEmail: string | null;
  resolvedSiteCode: string | null;
  employeeCategory: string | null;
}

// ── Workflow ────────────────────────────────────────────────

export enum RenewalDecisionStatus {
  Renew = 'Renew',
  Renegotiate = 'Renegotiate',
  Terminate = 'Terminate',
  Undecided = 'Undecided',
}

export interface FlagForReview {
  flagId: number;
  entityType: string;
  entityId: number;
  flaggedBy: string;
  assignedReviewer: string;
  reason: string;
  isResolved: boolean;
  resolutionNotes: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface RenewalDecision {
  decisionId: number;
  contractId: number;
  decisionStatus: RenewalDecisionStatus;
  decisionNotes: string;
  decidedBy: string;
  decisionDate: string;
}

// ── Onboarding ──────────────────────────────────────────────

export enum ChecklistItemStatus {
  Pending = 'Pending',
  Complete = 'Complete',
  Waived = 'Waived',
}

export interface OnboardingChecklist {
  checklistId: number;
  vendorId: number;
  templateId: number | null;
  createdAt: string;
}

export interface ChecklistItem {
  checklistItemId: number;
  checklistId: number;
  taskDescription: string;
  status: ChecklistItemStatus;
  completedBy: string | null;
  completedDate: string | null;
  waiverJustification: string | null;
  egnyteFilePath: string | null;
  sortOrder: number;
}

// ── Playbook ────────────────────────────────────────────────

export interface PlaybookRule {
  ruleId: number;
  category: string;
  ruleName: string;
  standardPosition: string;
  riskDescription: string | null;
  severity: string;
  isEnabled: boolean;
  appliesTo: string | null;
  lastModifiedBy: string | null;
  lastModifiedDate: string;
}

export interface RedlineSuggestion {
  suggestionId: number;
  contractId: number;
  ruleId: number;
  originalClauseText: string | null;
  suggestedRevision: string | null;
  riskRating: string;
  explanation: string | null;
  userDecision: string | null;
  decisionBy: string | null;
  decisionAt: string | null;
  createdAt: string;
}

// ── Search ──────────────────────────────────────────────────

export interface SearchResult {
  entityType: string;
  entityId: number;
  title: string;
  subtitle: string | null;
  matchedText: string | null;
  score: number;
}

export interface SearchResultGroup {
  entityType: string;
  results: SearchResult[];
  totalCount: number;
}

// ── Document Version History ────────────────────────────────

export interface DocumentVersion {
  entryId: string;
  versionNumber: number;
  size: number;
  lastModified: string;
  uploadedBy: string;
  isCurrent: boolean;
}

export interface DocumentVersionHistoryResponse {
  versions: DocumentVersion[];
  egnyteFilePath: string;
  egnyteLink: string;
}

// ── Error ───────────────────────────────────────────────────

export interface ErrorResponse {
  error: {
    code: string;
    message: string;
    details?: FieldError[];
    requestId: string;
  };
}

export interface FieldError {
  field: string;
  message: string;
  code: string;
}

// ── Audit ───────────────────────────────────────────────────

export interface AuditLogEntry {
  auditId: number;
  timestamp: string;
  userId: string;
  operationType: string;
  entityType: string;
  entityId: string;
  description: string | null;
  previousValues: string | null;
  newValues: string | null;
  bulkOperationId: string | null;
}

// ── Spend Anomaly ───────────────────────────────────────────

export interface SpendAnomaly {
  anomalyId: number;
  anomalyType: string;
  vendorId: number | null;
  affectedPeriod: string | null;
  expectedAmount: number | null;
  actualAmount: number | null;
  deviationPercentage: number | null;
  recommendedAction: string | null;
  severity: Severity | null;
  isResolved: boolean;
  createdAt: string;
}
